import { motion, useAnimationControls, useReducedMotion } from "framer-motion"
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { QR } from "../../components/QR"
import OnboardLayout from "./OnboardLayout"
import { useOnboard } from "./OnboardContext"
import { useTheme } from "../../theme/ThemeProvider"
import { Callout } from "../../components/onboard/Callout"

export function scramble(value: string): string {
  const digits = value.replace(/\d/g, "#")
  const random = digits.replace(/#/g, () => String(Math.floor(Math.random() * 10)))
  return value
    .split("")
    .map((char, index) => (/[0-9]/.test(char) ? random[index] : char))
    .join("")
}

export default function NumberAllocStep() {
  const { assignedNumber, refetchTenant } = useOnboard()
  const navigate = useNavigate()
  const [displayNumber, setDisplayNumber] = useState(scramble(assignedNumber))
  const [copied, setCopied] = useState(false)
  const copyTimer = useRef<number | null>(null)
  const controls = useAnimationControls()
  const shouldReduce = useReducedMotion()
  const { theme } = useTheme()
  const persona = theme === "fairy" ? "Fairy" : "Demon"
  const greeting = theme === "fairy" ? "Hi%20Fairy" : "Hi%20Demon"
  const accentGlow = theme === "fairy" ? "rgba(16,185,129,0.45)" : "rgba(255,43,79,0.45)"

  // Consider ready if we have a valid phone number assigned (starts with +)
  // The status check is removed since the number assignment is the real indicator
  const isReady = assignedNumber && assignedNumber.startsWith("+") && assignedNumber.length > 10

  const smsLink = useMemo(() => `sms:${assignedNumber}?body=${greeting}`, [assignedNumber, greeting])

  useEffect(() => {
    if (isReady) return
    const timer = setInterval(() => {
      refetchTenant()
    }, 3000)
    return () => clearInterval(timer)
  }, [isReady, refetchTenant])

  useEffect(() => {
    if (shouldReduce) {
      setDisplayNumber(assignedNumber)
      return
    }
    let frame = 0
    const interval = setInterval(() => {
      frame += 1
      if (frame > 10) {
        clearInterval(interval)
        setDisplayNumber(assignedNumber)
      } else {
        setDisplayNumber(scramble(assignedNumber))
      }
    }, 60)
    return () => clearInterval(interval)
  }, [assignedNumber, shouldReduce])

  useEffect(() => {
    if (shouldReduce) return
    controls.start({ scale: [1, 1.1, 1], transition: { duration: 0.8, ease: "easeOut" } })
  }, [controls, shouldReduce, assignedNumber])

  useEffect(() => {
    return () => {
      if (copyTimer.current) {
        window.clearTimeout(copyTimer.current)
      }
    }
  }, [])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(assignedNumber)
      setCopied(true)
      if (copyTimer.current) {
        window.clearTimeout(copyTimer.current)
      }
      copyTimer.current = window.setTimeout(() => {
        setCopied(false)
      }, 1600)
      controls.start({
        scale: [1, 1.04, 1],
        boxShadow: ["0 0 0", `0 0 22px ${accentGlow}`, "0 0 0"],
        transition: { duration: 0.6 },
      })
    } catch {
      /* noop */
    }
  }

  const onContinue = () => {
    navigate("/app")
  }

  if (!isReady) {
    return (
      <OnboardLayout
        title={`Summoning your ${persona}...`}
        subtitle="We’re provisioning a dedicated local business number for your agent."
        showEnterHint={false}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "24px", alignItems: "center", minHeight: "300px", justifyContent: "center" }}>
          <div 
            style={{
              width: "40px",
              height: "40px",
              border: "3px solid var(--rail)",
              borderTopColor: "var(--accent)",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
          <Callout tone="info">This usually takes about 10-20 seconds.</Callout>
        </div>
      </OnboardLayout>
    )
  }

  return (
    <OnboardLayout
      title={`Your ${persona} number`}
      subtitle="Save this number. You text it; the agent replies. Customers never see it."
      enterLabel="Continue to dashboard"
      onAdvance={onContinue}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "32px",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          width: "100%",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "18px", flex: "1 1 300px", minWidth: "min(340px, 100%)" }}>
          <motion.div
            animate={controls}
            className="mono"
            style={{
              fontSize: "clamp(19px, 5.5vw, 30px)",
              letterSpacing: "0.14em",
              padding: "14px 18px",
              border: "2px solid var(--rail)",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              width: "100%",
            }}
          >
            <span>{displayNumber}</span>
            <button
              type="button"
              onClick={copy}
              className="small"
              style={{
                marginLeft: "auto",
                border: "1px solid var(--rail)",
                background: copied ? "var(--rail)" : "transparent",
                color: copied ? "#0a0a0b" : "var(--text)",
                padding: "4px 12px",
                cursor: "pointer",
                transition: "background 0.2s ease, color 0.2s ease, border 0.2s ease",
              }}
            >
              {copied ? "Copied!" : "Copy"}
            </button>
          </motion.div>

          <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
            <a className="btn-lg btn-primary mono" href={smsLink} target="_blank" rel="noreferrer">
              Text via SMS
            </a>
            <button type="button" className="btn-lg btn-secondary mono" onClick={onContinue}>
              Continue to dashboard
            </button>
          </div>
          <div className="mono small" style={{ color: "var(--muted)" }}>
            Tip: Save it to your contacts so it&apos;s always one text away.
          </div>
        </div>

        <div style={{ flex: "0 0 auto", alignSelf: "flex-start" }}>
          <QR value={smsLink} size={128} />
        </div>
      </div>
    </OnboardLayout>
  )
}
